import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import React from "react";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Zap, X, Search, Check, ChevronsUpDown, AlertCircle } from "lucide-react";
import { z } from 'zod';
import { supabase } from "@/integrations/supabase/client";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './AccessModal.css';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Flag } from "@/utils/flag";
import { COUNTRIES } from "@/utils/countries";

const requestAccessSchema = z.object({
  fullName: z.string()
    .trim()
    .min(3, 'Full name must be at least 3 characters')
    .max(100, 'Full name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name contains invalid characters'),
  email: z.string()
    .trim()
    .toLowerCase()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .refine(
      (email) => {
        const parts = email.split('@');
        if (parts.length !== 2) return false;
        const domain = parts[1];
        return domain.includes('.') && domain.split('.').pop()!.length >= 2;
      },
      'Email must have a valid domain (e.g., example.com)'
    ),
  phone: z.string()
    .refine(
      (value) => {
        if (!value) return true; // Optional field
        try {
          return isValidPhoneNumber(value);
        } catch {
          return false;
        }
      },
      'Please enter a valid phone number'
    )
    .optional(),
  reason: z.string()
    .trim()
    .min(10, 'Please provide at least 10 characters')
    .max(500, 'Reason must be less than 500 characters')
    .optional()
});

interface AccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AccessModal = ({ open, onOpenChange }: AccessModalProps) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateField = (fieldName: keyof typeof formData) => {
    try {
      const fieldSchema = requestAccessSchema.shape[fieldName as keyof typeof requestAccessSchema.shape];
      if (fieldSchema) {
        fieldSchema.parse(formData[fieldName]);
        setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
        setErrors(prev => ({ ...prev, [fieldName]: '' }));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors[0]?.message || 'Invalid value';
        setValidationErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
        setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setValidationErrors({});
    
    // Validate inputs
    const result = requestAccessSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      setValidationErrors(fieldErrors);
      toast.error(result.error.errors[0].message);
      return;
    }

    setLoading(true);
    
    try {
      // Split full name into first and last name
      const nameParts = result.data.fullName.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Store access request in database
      const { error: dbError } = await supabase
        .from('access_requests')
        .insert([
          {
            email: result.data.email,
            first_name: firstName,
            last_name: lastName,
            phone: result.data.phone || null,
            reason: result.data.reason || null,
            status: 'pending'
          }
        ]);

      if (dbError) {
        if (dbError.code === '23505') {
          toast.error('You have already submitted an access request with this email');
          return;
        }
        throw dbError;
      }

      // Send emails via edge function
      const { error: emailError } = await supabase.functions.invoke('send-access-request-emails', {
        body: {
          firstName: firstName,
          lastName: lastName,
          email: result.data.email,
          phone: result.data.phone,
          reason: result.data.reason
        }
      });

      if (emailError) {
        console.error('Error sending emails:', emailError);
        toast.error('Request submitted but emails failed to send');
      } else {
        toast.success('Access request submitted! Check your email.');
      }

      setSubmittedEmail(result.data.email);
      setShowSuccess(true);
      setFormData({ fullName: "", email: "", phone: "", reason: "" });
    } catch (error) {
      console.error('Error submitting access request:', error);
      toast.error('Failed to submit access request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!showSuccess) {
      onOpenChange(false);
      setFormData({ fullName: "", email: "", phone: "", reason: "" });
      setErrors({});
      setValidationErrors({});
    }
  };

  return (
    <>
      <Dialog open={open && !showSuccess} onOpenChange={handleClose}>
        <DialogContent className="bg-charcoal/95 backdrop-blur-xl border border-gold/20 text-ivory w-full max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] md:max-w-md p-4 sm:p-6 md:p-8 max-h-[85vh] overflow-y-auto overflow-x-visible">
          <DialogHeader className="space-y-2 sm:space-y-3 relative">
            <DialogTitle className="text-xl sm:text-2xl font-sans text-ivory flex items-center gap-2 sm:gap-3">
              <motion.span
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              >
                <Sparkles className="w-6 h-6 text-gold" />
              </motion.span>
              Request private access
            </DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              We onboard approved clients in 24-48 hours
            </DialogDescription>
          </DialogHeader>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {/* Full Name Field */}
              <div className="space-y-2">
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  inputMode="text"
                  spellCheck={false}
                  className={`w-full rounded-lg bg-charcoal/60 border ${
                    errors.fullName ? 'border-red-500/50' : 'border-ivory/10'
                  } px-4 py-3 text-ivory outline-none focus:border-gold transition-all placeholder:text-ivory/40`}
                  style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))', textIndent: '0px' }}
                  placeholder="Full name"
                  value={formData.fullName}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\s+/, '');
                    setFormData({ ...formData, fullName: value });
                    setErrors({ ...errors, fullName: "" });
                  }}
                  required
                />
                {errors.fullName && (
                  <p className="text-red-400 text-xs">{errors.fullName}</p>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    spellCheck={false}
                    className={`w-full rounded-lg bg-charcoal/60 border px-4 py-3 pr-10 text-ivory outline-none transition-all placeholder:text-ivory/40 ${
                      validationErrors.email ? 'border-red-400 focus:border-red-400' : 'border-ivory/10 focus:border-gold'
                    }`}
                    style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))', textIndent: '0px' }}
                    placeholder="Email address"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/^\s+/, '');
                      setFormData({ ...formData, email: value });
                      if (validationErrors.email) {
                        setValidationErrors({ ...validationErrors, email: "" });
                        setErrors({ ...errors, email: "" });
                      }
                    }}
                    onBlur={() => validateField('email')}
                    required
                  />
                  {validationErrors.email ? (
                    <AlertCircle className="absolute right-3 top-3.5 h-5 w-5 text-red-400" />
                  ) : formData.email.includes('@') && formData.email.includes('.') ? (
                    <CheckCircle2 className="absolute right-3 top-3.5 h-5 w-5 text-green-400" />
                  ) : null}
                </div>
                {validationErrors.email && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.email}
                  </p>
                )}
              </div>

              {/* Phone Field */}
              <div className="space-y-2">
                <div className="relative">
                  <PhoneInput
                    international
                    defaultCountry="GB"
                    value={formData.phone}
                    onChange={(value) => {
                      if (!value || value.replace(/\D/g, '').length <= 15) {
                        setFormData({ ...formData, phone: value || '' });
                        if (validationErrors.phone) {
                          setValidationErrors({ ...validationErrors, phone: "" });
                          setErrors({ ...errors, phone: "" });
                        }
                      }
                    }}
                    onBlur={() => validateField('phone')}
                    countrySelectComponent={({ value, onChange, options, disabled }) => {
                      const [triggerRef, setTriggerRef] = React.useState<HTMLButtonElement | null>(null);
                      const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({});

                      React.useEffect(() => {
                        if (!triggerRef) return;

                        const updateDropdownHeight = () => {
                          const rect = triggerRef.getBoundingClientRect();
                          const viewportHeight = window.innerHeight;
                          const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0');
                          
                          const spaceBelow = viewportHeight - rect.bottom - 8 - safeAreaBottom;
                          const spaceAbove = rect.top - 8 - safeAreaBottom;
                          
                          const maxHeight = Math.max(180, Math.min(viewportHeight * 0.6, spaceBelow));
                          
                          setDropdownStyle({
                            '--dropdown-max': `${maxHeight}px`,
                          } as React.CSSProperties);
                        };

                        updateDropdownHeight();
                        window.addEventListener('resize', updateDropdownHeight);
                        window.addEventListener('scroll', updateDropdownHeight, true);
                        
                        return () => {
                          window.removeEventListener('resize', updateDropdownHeight);
                          window.removeEventListener('scroll', updateDropdownHeight, true);
                        };
                      }, [triggerRef]);

                      return (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            ref={setTriggerRef}
                            type="button"
                            disabled={disabled}
                            className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gold/10 transition-all duration-200 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-gold/30"
                          >
                            <Flag code={(value as string) || "GB"} size={20} className="shadow-[0_0_0_1px_hsl(var(--gold)/.25)]" />
                            <ChevronsUpDown className="h-3 w-3 text-gold/70" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          style={dropdownStyle}
                          className="w-[calc(100vw-32px)] sm:w-[320px] p-0 bg-charcoal/98 border-2 border-gold/40 backdrop-blur-xl shadow-2xl shadow-gold/10 overflow-hidden phone-dropdown-container"
                          side="bottom"
                          align="start"
                          sideOffset={8}
                          onWheel={(e) => e.stopPropagation()}
                          onTouchStart={(e) => e.stopPropagation()}
                          onTouchMove={(e) => e.stopPropagation()}
                        >
                          <Command className="bg-transparent" filter={(value, search) => {
                            const country = COUNTRIES.find(c => c.code.toLowerCase() === value.toLowerCase());
                            if (!country) return 0;
                            const searchLower = search.toLowerCase();
                            if (country.label.toLowerCase().includes(searchLower)) return 1;
                            if (country.code.toLowerCase().includes(searchLower)) return 1;
                            if (country.dialCode.includes(searchLower)) return 1;
                            return 0;
                          }}>
                            <div className="flex items-center border-b border-gold/20 px-3 bg-charcoal/60">
                              <Search className="mr-2 h-4 w-4 shrink-0 text-gold" />
                              <CommandInput 
                                placeholder="Search by name, code, or +44..." 
                                className="h-11 bg-transparent border-0 text-ivory placeholder:text-ivory/40 focus:ring-0"
                              />
                            </div>
                            <CommandList 
                              className="overflow-y-auto overscroll-contain scrollbar-thin scrollbar-thumb-gold/20 scrollbar-track-transparent phone-dropdown-scroll"
                              style={{ maxHeight: 'var(--dropdown-max, 256px)' }}
                            >
                              <CommandEmpty className="py-8 text-center text-sm text-ivory/50">
                                No country found.
                              </CommandEmpty>
                              <CommandGroup className="p-1.5">
                                {COUNTRIES.map((country) => (
                                  <CommandItem
                                    key={country.code}
                                    value={country.code}
                                    onSelect={() => {
                                      onChange(country.code);
                                    }}
                                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-lg hover:bg-gold/15 text-ivory aria-selected:bg-gold/20 transition-all duration-200 group"
                                  >
                                    <Flag code={country.code} size={22} />
                                    <div className="flex-1 flex items-center justify-between min-w-0">
                                      <span className="text-sm font-medium truncate">{country.label}</span>
                                      <span className="text-xs text-gold/90 font-mono ml-2 flex-shrink-0">{country.dialCode}</span>
                                    </div>
                                    <Check
                                      className={`h-4 w-4 text-gold transition-opacity ${value === country.code ? "opacity-100" : "opacity-0"}`}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                          </PopoverContent>
                        </Popover>
                      );
                    }}
                    className={`phone-input-luxury ${validationErrors.phone ? 'phone-error' : ''}`}
                  />
                  {formData.phone && (
                    <div className="absolute right-3 top-3.5 z-10">
                      {validationErrors.phone ? (
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      ) : formData.phone && isValidPhoneNumber(formData.phone) ? (
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                      ) : formData.phone ? (
                        <AlertCircle className="h-5 w-5 text-amber-400" />
                      ) : null}
                    </div>
                  )}
                </div>
                {validationErrors.phone && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.phone}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Phone number is optional
                </p>
              </div>

              {/* Reason Field */}
              <div className="space-y-2">
                <textarea
                  id="reason"
                  className={`w-full rounded-lg bg-charcoal/60 border ${
                    errors.reason ? 'border-red-500/50' : 'border-ivory/10'
                  } px-4 py-3 text-ivory text-sm outline-none focus:border-gold transition-all min-h-[100px] resize-none placeholder:text-ivory/40`}
                  placeholder="Why do you want to join Exora Club?"
                  value={formData.reason}
                  onChange={(e) => {
                    setFormData({ ...formData, reason: e.target.value });
                    setErrors({ ...errors, reason: "" });
                  }}
                />
                {errors.reason && (
                  <p className="text-red-400 text-xs">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full rounded-lg bg-gold hover:bg-gold-glow px-6 py-3.5 text-charcoal font-semibold text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group shine-button"
              >
                <span className="relative z-10">{loading ? "Submitting..." : "Request access"}</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{
                    x: ['-200%', '200%']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 1,
                    ease: "easeInOut"
                  }}
                />
              </motion.button>
            </form>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-charcoal/95 backdrop-blur-md overflow-hidden"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, rotateY: 90 }}
              animate={{ scale: 1, opacity: 1, rotateY: 0 }}
              exit={{ scale: 0.8, opacity: 0, rotateY: -90 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 20,
                duration: 0.6
              }}
              className="bg-gradient-to-br from-card/95 to-card/80 backdrop-blur-xl border-2 border-gold/40 rounded-3xl p-12 max-w-lg w-full text-center relative overflow-hidden shadow-2xl shadow-gold/30"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                onClick={() => {
                  setShowSuccess(false);
                  onOpenChange(false);
                }}
                className="absolute top-4 right-4 text-ivory/60 hover:text-gold transition-colors z-10 cursor-pointer"
              >
                <X className="w-6 h-6" />
              </motion.button>

              {/* Animated background */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-gold/10 via-transparent to-gold/5"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              
              {/* Floating particles */}
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-gold/50 rounded-full"
                  initial={{ x: "50%", y: "50%", scale: 0 }}
                  animate={{
                    x: `${50 + Math.cos(i * 30 * Math.PI / 180) * 120}%`,
                    y: `${50 + Math.sin(i * 30 * Math.PI / 180) * 120}%`,
                    scale: [0, 1.5, 0],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 2.5,
                    delay: i * 0.08,
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                />
              ))}

              {/* Main icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  delay: 0.2,
                  type: "spring",
                  stiffness: 200,
                  damping: 15
                }}
                className="w-32 h-32 mx-auto mb-6 relative"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.4, 0, 0.4] 
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute inset-0 bg-gold/30 rounded-full blur-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-gold/40 to-gold/10 rounded-full flex items-center justify-center border-2 border-gold/30">
                  <CheckCircle2 className="w-20 h-20 text-gold relative z-10" strokeWidth={2.5} />
                </div>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="absolute -top-2 -right-2"
                >
                  <Sparkles className="w-10 h-10 text-gold drop-shadow-lg" />
                </motion.div>
              </motion.div>

              {/* Text content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <h2 className="text-4xl font-sans font-bold text-ivory mb-3 flex items-center justify-center gap-3">
                  Welcome aboard!
                  <motion.span
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                  >
                    <Zap className="w-9 h-9 text-gold drop-shadow-glow" />
                  </motion.span>
                </h2>
                <p className="text-xl text-gold/90 font-semibold mb-4">
                  Your access request has been received
                </p>
                <p className="text-muted-foreground mb-2 max-w-md mx-auto leading-relaxed">
                  Our exclusive concierge team will review your application and reach out within{" "}
                  <span className="text-gold font-bold">24-48 hours</span>
                </p>
                <p className="text-sm text-ivory/60 mb-6">
                  Confirmation sent to: <span className="text-ivory font-medium">{submittedEmail}</span>
                </p>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="bg-gold/10 border border-gold/30 rounded-xl p-5 mb-6 backdrop-blur-sm"
                >
                  <p className="text-sm text-ivory/90 font-medium">
                    ✨ Check your inbox for confirmation
                  </p>
                </motion.div>

                <button
                  type="button"
                  onClick={() => {
                    setShowSuccess(false);
                    onOpenChange(false);
                  }}
                  className="relative bg-gold hover:bg-gold-glow text-charcoal font-bold px-10 py-4 rounded-full text-base shadow-xl shadow-gold/40 transition-all cursor-pointer pointer-events-auto"
                  style={{ zIndex: 10 }}
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
